import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import LoaderOverlay from "./LoaderOverlay";
import UnauthorizedAccess from "./UnauthorizedAccess";

interface RequirePermissionProps {
  requiredPermission?: string;
  children?: React.ReactNode;
}

const RequirePermission = ({
  requiredPermission,
  children,
}: RequirePermissionProps) => {
  const { admin, isLoading } = useAuth();

  if (isLoading) {
    return <LoaderOverlay />;
  }

  if (!admin) {
    return <Navigate to="/login" replace />;
  }

  // If no specific permission required, just being an admin is enough (already checked by ProtectedRoute usually, but good to be safe)
  if (!requiredPermission) {
    return children ? <>{children}</> : <Outlet />;
  }

  // Check if admin has the required permission
  // We assume 'super_admin' or similar might have all permissions, but for now let's stick to the list.
  // If admin.permissions includes the required permission
  const hasPermission =
    admin.permissions?.includes(requiredPermission) ||
    admin.role === "super_admin"; // Fallback for super admin

  if (!hasPermission) {
    return <UnauthorizedAccess />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default RequirePermission;
